<template>
    <tr class="text-xs-center nowrap weekRow">
        <td class="pa-0 firstWeekRow">
            <div class="cell">
                <v-avatar 
                    v-if="item.locationHasAlert" 
                    :color="item.locationAlertLevelColor"
                    class="mr-1"
                    size="24">
                </v-avatar>
                <span>{{ item.locationName }}</span>
            </div>
        </td>
        <td class="pa-0">
            <div class="cell">
                <v-btn flat block @click="$emit('goToDetail')">
                    <v-avatar 
                        v-if="item.rotationDays" 
                        color="primary"
                        class="mr-1"
                        size="24">
                        <span class="white--text">{{ item.rotationDays }}</span>
                    </v-avatar>
                    <span>{{ item.varietyName }}</span>
                </v-btn>
            </div>
        </td>
        <td class="pa-0">
            <div v-if="showYieldValues" class="small-cell">TY</div>
            <div v-if="showProfileValues" class="small-cell">P</div>
            <div class="small-cell">F</div>
            <div class="small-cell" v-if="!isFutureWeek">A</div>
        </td>
        <td v-for="(forecastWeek, index) in item.forecastWeeks" 
            :key="index" 
            :id="'week-'+ item.cropLocationId + '-' + item.varietyId + '-' + index"
            :class="`${forecastWeek.isActiveWeek ? `info lighten-4` : ``}`"
            style="min-width: 100px; padding: 0px; position:relative;"
            align="center"
            :tabindex="editable && !forecastWeek.locked ? 0 : null"
            @focus="openEdit(index)"
            @keydown.esc="closeEdit(index, false)"
            @click="openEdit(index)">
            <template>
                <div v-if="hasNonZeroSpray(forecastWeek)" class="info-mark error--text">
                    !
                </div>
                <div v-if="showYieldValues" class="small-cell">
                    <v-layout align-center justify-center row fill-height>
                        <v-checkbox tabindex="-1" v-if="`${forecastWeek.totalYields}` && !forecastWeek.locked" :input-value="isYieldChecked(forecastWeek)" hide-details v-on:click.stop.prevent="applyYield(forecastWeek)"></v-checkbox>
                        <span v-if="`${forecastWeek.totalYields}`">{{forecastWeek.totalYields}}</span>
                        <span v-else>N/A</span>
                    </v-layout>
                </div>
                <div v-if="showProfileValues" class="small-cell">
                    <v-layout align-center justify-center row fill-height>
                        <v-checkbox tabindex="-1" v-if="`${forecastWeek.totalProfiles}` && !forecastWeek.locked" :input-value="isProfileChecked(forecastWeek)" hide-details v-on:click.stop.prevent="applyProfile(forecastWeek)"></v-checkbox>
                        <span v-if="`${forecastWeek.totalProfiles}`">{{forecastWeek.totalProfiles}}</span>
                        <span v-else>N/A</span>
                    </v-layout>
                </div>
                <div v-if="editMode.indexOf(index) >= 0">
                    <EditTableCell
                        style="width: 100px"
                        label="Forecast"
                        :value="`${forecastWeek.total}`"
                        @input="modifyWeekForecastValue(index, $event)"
                        @close="closeEdit(index, $event)"
                        :rules="[validateForecastValue]"
                        :loading="loading.indexOf(index) >= 0"
                        :dense="true">
                    </EditTableCell>
                </div>
                <div v-else class="small-cell font-weight-bold">
                    <v-layout align-center justify-center row fill-height>
                        <div v-if="`${forecastWeek.total}`">{{ forecastWeek.total }}</div>
                        <div v-else>N/A</div>
                        <v-icon v-if="forecastWeek.locked">lock_outline</v-icon>
                        <v-tooltip v-model="forecastWeek.showNuPointSprayDetails" top>
                            <template v-slot:activator="{ on, attrs }">                            
                                <span><v-img v-if="forecastWeek.hasNuPointSprayWithholding" :src="require('@/assets/spray.png')" width="24px" @click="forecastWeek.showNuPointSprayDetails = !forecastWeek.showNuPointSprayDetails" v-bind="attrs" v-on="on"></v-img></span>
                                <span><v-chip dark v-if="forecastWeek.hasNuPointSprayICA" :color="forecastWeek.isNuPointSprayICAProtected ? 'green': 'red'" small v-bind="attrs" v-on="on" class="ica-icon">ICA</v-chip></span>
                            </template>
                            <NuPointSprayDetails v-if="forecastWeek.hasNuPointSprayWithholding || forecastWeek.hasNuPointSprayICA" :nuPointSprayModels="forecastWeek.forecastDays.map(d => d.nuPointSprays).flat()" :show="forecastWeek.showNuPointSprayDetails" />
                        </v-tooltip>
                    </v-layout>
                </div>
                <div class="small-cell" v-if="!isFutureWeek"><span>{{parseInt(forecastWeek.totalActuals)}}</span></div>
            </template>
        </td>
    </tr>
</template>

<script>
import EditTableCell from '@/components/forecasting/EditTableCell.vue'
import ForecastingDistributionService from '@/services/forecastingDistributionService'
import NuPointSprayDetails from "@/components/nuPointSpray/NuPointSpray.vue"

import { mapGetters, mapActions, mapState } from "vuex"

export default {
    components: {
        EditTableCell,
        NuPointSprayDetails
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        forecastWeeksYieldApplied: {
            type: Array,
            required: true
        },
        forecastWeeksProfileApplied: {
            type: Array,
            required: true
        }
    },
    data(){
        return {
            editMode: [],
            loading: [],
            isCheck: false
        };
    },
    computed: {
        ...mapGetters("ForecastingStore", [
            "editable"
        ]),
        ...mapState("ForecastingStore", [
            "showProfileValues",
            "showYieldValues"
        ]),
        isFutureWeek() {
            return this.item && this.item.forecastWeeks.length > 1 && this.item.forecastWeeks[1].isFutureWeek;
        }
    },
    methods: {
        ...mapActions("ForecastingStore", [
            "handleForecastsChanged",
            "updateData"
        ]),
        openEdit(index){           
            if(!this.editable){
                return;
            }

            let forecastWeek = this.item.forecastWeeks[index];
            if(forecastWeek.locked){
                return;
            }

            if(this.editMode.indexOf(index) < 0){
                this.editMode.push(index);
            }
        },
        closeEdit(index, isEnter){
            if(this.editMode.indexOf(index) >= 0){
                this.editMode.splice(this.editMode.indexOf(index), 1);
            }
            if(isEnter && this.item.next) {
                document.getElementById('week-'+ this.item.next.cropLocationId + '-' + this.item.next.varietyId + '-' + index).click();
            }
        },
        validateForecastValue(v){
            if(v == null || !v.match(/^[0-9]{1,5}$/)){
                return "Please enter a number in the form XXXXX";
            }

            return false;
        },
        async modifyWeekForecastValue(index, event){
            var newTotal = event.value;
            var isEnter = event.isEnter;
            if(this.loading.indexOf(index) < 0){
                this.loading.push(index);
            }

            let forecastWeek = this.item.forecastWeeks[index];
            if(!forecastWeek.locked){
                let dayValues = ForecastingDistributionService.getNewDayValuesForForecastWeek(
                    newTotal, 
                    forecastWeek.forecastDays,
                    forecastWeek.week
                );
                var bulkUpdateForecasts = [];
                for(let i = 0; i < dayValues.length; i++){
                    var modifiedForecast = this.modifyForecastValue(
                        dayValues[i].day, 
                        dayValues[i].newValue, 
                        forecastWeek.forecastDays, 
                        false
                    );
                    if(modifiedForecast != null){
                        bulkUpdateForecasts.push(modifiedForecast);
                    }
                }

                await this.handleForecastsChanged({
                    forecasts: bulkUpdateForecasts
                });
                await this.updateData({all: false, forecastDays: forecastWeek.forecastDays});
            }

            if(this.loading.indexOf(index) >= 0){
                this.loading.splice(this.loading.indexOf(index), 1);
            }
            this.closeEdit(index, isEnter);
        },
        modifyForecastValue(day, newValue, forecastDays, allowZeroes){
            let forecastDay = forecastDays.find(x => x.day == day);

            // Start without any changes
            let newForecastValue = forecastDay.forecastValue;
            let newAdjustedValue = forecastDay.adjustedValue;

            if(!forecastDay.forecastValueLocked){
                // Update the forecast value
                newForecastValue = newValue;

                // If the forecast value was blank and it is now being set to 0
                // Leave it as blank
                if(newForecastValue === 0 && forecastDay.forecastValue === "" && !allowZeroes){
                    newForecastValue = "";
                }
            } else if(!forecastDay.adjustedValueLocked){
                // Update the adjusted value
                newAdjustedValue = newValue;

                // If the adjusted value was blank and it is now being set to 0
                // Leave it as blank
                if(newAdjustedValue === 0 && forecastDay.adjustedValue === "" && !allowZeroes){
                    newAdjustedValue = "";
                }
            }

            // If the values haven't changed, no need to send a modification
            if(newForecastValue === forecastDay.forecastValue 
            && newAdjustedValue === forecastDay.adjustedValue){
                return null;
            }

            // Return a modification
            return {
                forecastDate: day,
                forecastValue: newForecastValue === "" ? null : newForecastValue,
                adjustedValue: newAdjustedValue === "" ? null : newAdjustedValue,
                cropLocationId: this.item.cropLocationId,
                varietyId: this.item.varietyId
            };
        },
        hasNonZeroSpray(forecastWeek) {
            return forecastWeek.forecastDays.some(forecastDay=> {
                let value = forecastDay.forecastValueLocked && forecastDay.adjustedValue !== '' ? forecastDay.adjustedValue : forecastDay.forecastValue;
                return forecastDay.isSpray && value > 0;
            });
        },
        applyYield(forecastWeek) {
            this.$emit("onYieldsApplied", { week: forecastWeek.week, cropLocationId: this.item.cropLocationId, varietyId: this.item.varietyId});
        },
        isYieldChecked(forecastWeek) {
            return this.forecastWeeksYieldApplied.findIndex(y=> y.week == forecastWeek.week && y.cropLocationId == this.item.cropLocationId && y.varietyId == this.item.varietyId) > -1;
        },        
        isProfileChecked(forecastWeek) {
            return this.forecastWeeksProfileApplied.findIndex(y=> y.week == forecastWeek.week && y.cropLocationId == this.item.cropLocationId && y.varietyId == this.item.varietyId) > -1;
        },
        applyProfile(forecastWeek) {
            this.$emit("onProfilesApplied", { week: forecastWeek.week, cropLocationId: this.item.cropLocationId, varietyId: this.item.varietyId});
        }
    },
    created(){
        
    }
}
</script>

<style scoped>
    .ica-icon {
        font-size: 11px;
        width: 26px;
        justify-content: center;
    }

    .nowrap {
        white-space: nowrap;
    }

    .cell {
        height: 60px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
    }

    .small-cell {
        min-height: 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

     .firstWeekRow {
        position: -webkit-sticky;
        position: sticky;
        left: 0;
        z-index: 10;
        background: white;
    }

    .info-mark {
        position: absolute;
        top: 0;
        right: 0;
        margin-right: 4px;
        font-weight: bold;
        font-size: 15px;
    }

    table tbody td {
        font-size:18px;
    }


</style>